/**
 * The request-type field model behind RequestTypeFormBuilder's field editor and field card.
 *
 * Carried from the pre-November form builder (FieldDialog, FieldConfigurationWizard,
 * DraggableFieldItem) in the October 2026 restore (docs/product-audit/RESTORE_PLAN.md, R1.1):
 * every field type the old editor offered, a default value, a show/hide condition with an
 * operator, cascading parents and children, automation triggers, a customer-facing portal
 * label, pasted options, hide-from-portal with a fixed value, the reporting and automation
 * flags, the "needs a custom field" rule, the consultant-review flag for Asset fields, the
 * duplicate-name check, and "Save only when something changed".
 *
 * `FormField` is the builder's in-memory shape; `toSavedField` / `fromSavedField` convert to
 * and from the record the server keeps (`fields[]` on a request type).
 */

export type ConditionOperator = 'equals' | 'not_equals' | 'contains';

export interface CascadingOption {
  parentValue: string;
  childValues: string[];
}

export interface AutomationTrigger {
  id: string;
  triggerValue: string;
  automationDescription?: string;
}

export interface FormField {
  id: string;
  name: string;
  /** Customer-facing title on the portal, when it differs from the back-end name. */
  portalLabel?: string;
  type: string;
  required: boolean;
  helpText?: string;
  options?: string[];
  /** The raw pasted text behind `options`, kept so a reopened field shows what was typed. */
  optionsRawInput?: string;
  cascadingOptions?: CascadingOption[];
  defaultValue?: string;
  conditionalParent?: string;
  conditionalOperator?: ConditionOperator;
  conditionalValue?: string;
  hideAndPrepopulate?: boolean;
  prepopulatedValue?: string;
  automationTriggers?: AutomationTrigger[];
  usedForReporting?: boolean;
  usedForAutomation?: boolean;
  automationUseCase?: string;
  flaggedForConsultant?: boolean;
  consultantReviewReason?: string;
  order: number;
  isSystem?: boolean;
  aiGenerated?: boolean;
}

export interface FieldTypeOption {
  value: string;
  label: string;
  group: 'Text' | 'Choice' | 'People' | 'Date and time' | 'Other';
}

/** Every type the old FieldDialog (16) and the live builder (17) offered, as one list. */
export const FIELD_TYPES: FieldTypeOption[] = [
  { value: 'short-text', label: 'Short text', group: 'Text' },
  { value: 'paragraph', label: 'Paragraph', group: 'Text' },
  { value: 'number', label: 'Number', group: 'Text' },
  { value: 'email', label: 'Email', group: 'Text' },
  { value: 'url', label: 'URL', group: 'Text' },
  { value: 'dropdown', label: 'Dropdown (single choice)', group: 'Choice' },
  { value: 'multiselect', label: 'Dropdown (multiple choices)', group: 'Choice' },
  { value: 'cascading', label: 'Cascading dropdown (parent and child)', group: 'Choice' },
  { value: 'radio', label: 'Radio buttons', group: 'Choice' },
  { value: 'checkboxes', label: 'Checkboxes', group: 'Choice' },
  { value: 'user-single', label: 'User picker (single user)', group: 'People' },
  { value: 'user-multiple', label: 'User picker (multiple users)', group: 'People' },
  { value: 'group-single', label: 'Group picker (single group)', group: 'People' },
  { value: 'group-multiple', label: 'Group picker (multiple groups)', group: 'People' },
  { value: 'date', label: 'Date', group: 'Date and time' },
  { value: 'datetime', label: 'Date and time', group: 'Date and time' },
  { value: 'time', label: 'Time', group: 'Date and time' },
  { value: 'asset', label: 'Assets object (requires JSM Assets)', group: 'Other' },
  { value: 'attachment', label: 'Attachment', group: 'Other' },
];

/** Older records used the pre-November type names; read them as today's. */
const LEGACY_TYPE_MAP: Record<string, string> = {
  'number-field': 'number',
  'url-field': 'url',
  'date-picker': 'date',
  'datetime-picker': 'datetime',
  'select-single': 'dropdown',
  'select-multiple': 'multiselect',
  'select-cascading': 'cascading',
  'radio-buttons': 'radio',
  'user-picker-single': 'user-single',
  'user-picker-multiple': 'user-multiple',
  'group-picker-single': 'group-single',
  'group-picker-multiple': 'group-multiple',
  assets: 'asset',
  short_text: 'short-text',
  select_single: 'dropdown',
  select_multi: 'multiselect',
  cascading_select: 'cascading',
  radio_buttons: 'radio',
  user_picker_single: 'user-single',
  user_picker_multi: 'user-multiple',
  group_picker: 'group-single',
  date_time: 'datetime',
};

export function normalizeFieldType(type: string | undefined): string {
  if (!type) return 'short-text';
  return LEGACY_TYPE_MAP[type] || type;
}

export function fieldTypeLabel(type: string): string {
  const t = normalizeFieldType(type);
  return FIELD_TYPES.find((f) => f.value === t)?.label || type;
}

/** Types whose values are a flat list the consultant types in. For Assets the list is the asset types. */
export const TYPES_WITH_OPTIONS = ['dropdown', 'multiselect', 'radio', 'checkboxes', 'asset'];
/** Types a trigger can hang off: a chosen value fires an automation. */
export const TYPES_WITH_TRIGGERS = ['dropdown', 'multiselect', 'radio', 'checkboxes', 'cascading'];

export const CONDITION_OPERATORS: { value: ConditionOperator; label: string }[] = [
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'does not equal' },
  { value: 'contains', label: 'contains' },
];

export function operatorLabel(op: ConditionOperator | undefined): string {
  return CONDITION_OPERATORS.find((o) => o.value === (op || 'equals'))?.label || 'equals';
}

/**
 * Options pasted one per line, comma-separated, or tab-separated (from Excel).
 * Lines win over commas so a value such as "Smith, Jane" survives a one-per-line paste;
 * a single line is split on tabs, then commas. Blank entries are dropped, and repeats keep the first.
 */
export function parseOptionList(input: string | undefined | null): string[] {
  if (!input || !input.trim()) return [];
  let parts: string[];
  if (/\r?\n/.test(input.trim())) parts = input.split(/\r?\n/);
  else if (input.includes('\t')) parts = input.split('\t');
  else parts = input.split(',');
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of parts) {
    const v = raw.trim();
    if (!v) continue;
    const key = v.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(v);
  }
  return out;
}

export function hasOtherOption(options: string[] | undefined): boolean {
  return (options || []).some((o) => o.trim().toLowerCase() === 'other');
}

/** A field that is reported on, automated on, hidden with a fixed value, or an Assets object needs a Jira custom field. */
export function needsCustomField(field: Pick<FormField, 'type' | 'usedForReporting' | 'usedForAutomation' | 'hideAndPrepopulate'>): boolean {
  return Boolean(
    field.usedForReporting ||
      field.usedForAutomation ||
      field.hideAndPrepopulate ||
      normalizeFieldType(field.type) === 'asset',
  );
}

/** Another field already carries this name (case-insensitive, ignoring the field itself). */
export function isDuplicateFieldName(name: string, fieldId: string, fields: Pick<FormField, 'id' | 'name'>[]): boolean {
  const key = name.trim().toLowerCase();
  if (!key) return false;
  return fields.some((f) => f.id !== fieldId && (f.name || '').trim().toLowerCase() === key);
}

/** The values another field can be compared against: its options, or its cascading parents. */
export function fieldValueChoices(field: Pick<FormField, 'type' | 'options' | 'cascadingOptions'> | undefined): string[] {
  if (!field) return [];
  if (normalizeFieldType(field.type) === 'cascading') {
    return (field.cascadingOptions || []).map((c) => c.parentValue).filter((v) => v.trim());
  }
  return (field.options || []).filter((v) => v.trim());
}

/** The problems that stop a field from being added; the old dialogs refused each of these. */
export function validateField(field: FormField, allFields: FormField[]): string[] {
  const errors: string[] = [];
  const type = normalizeFieldType(field.type);
  if (!field.name?.trim()) errors.push('Field name is required');
  else if (isDuplicateFieldName(field.name, field.id, allFields)) errors.push('A field with this name already exists');
  if (!field.isSystem && TYPES_WITH_OPTIONS.includes(type) && (field.options || []).filter((o) => o.trim()).length === 0) {
    errors.push(type === 'asset' ? 'List at least one asset type' : 'Add at least one option');
  }
  if (type === 'cascading') {
    const parents = (field.cascadingOptions || []).filter((c) => c.parentValue.trim());
    if (parents.length === 0) errors.push('Add at least one parent option');
    for (const p of parents) {
      if (p.childValues.filter((v) => v.trim()).length === 0) errors.push(`Parent option "${p.parentValue.trim()}" needs child options`);
    }
  }
  if (field.hideAndPrepopulate && !field.prepopulatedValue?.trim()) errors.push('A hidden field needs the fixed value it is filled with');
  if (field.conditionalParent) {
    if (field.conditionalParent === field.id) errors.push('A field cannot depend on itself');
    else if (!allFields.some((f) => f.id === field.conditionalParent)) errors.push('The condition points at a field that no longer exists');
    if (!field.conditionalValue?.trim()) errors.push('The condition needs a value to compare against');
  }
  if (field.usedForAutomation && !field.automationUseCase?.trim()) errors.push('Describe how the field is used in automation');
  return errors;
}

/** Trim and tidy a field before it joins the form; derived flags are worked out here. */
export function finalizeField(field: FormField): FormField {
  const type = normalizeFieldType(field.type);
  const next: FormField = { ...field, type, name: field.name.trim() };
  next.portalLabel = field.portalLabel?.trim() || undefined;
  next.helpText = field.helpText?.trim() || undefined;
  next.defaultValue = field.defaultValue?.trim() || undefined;
  if (TYPES_WITH_OPTIONS.includes(type)) {
    next.options = (field.options || []).map((o) => o.trim()).filter(Boolean);
  } else if (type === 'cascading') {
    next.cascadingOptions = (field.cascadingOptions || [])
      .filter((c) => c.parentValue.trim())
      .map((c) => ({ parentValue: c.parentValue.trim(), childValues: c.childValues.map((v) => v.trim()).filter(Boolean) }));
    // The parents double as the flat option list so anything that only reads `options` still sees them.
    next.options = next.cascadingOptions.map((c) => c.parentValue);
    next.optionsRawInput = undefined;
  } else {
    next.options = undefined;
    next.optionsRawInput = undefined;
    next.cascadingOptions = undefined;
  }
  if (type !== 'cascading') next.cascadingOptions = undefined;
  if (!field.conditionalParent) {
    next.conditionalParent = undefined;
    next.conditionalOperator = undefined;
    next.conditionalValue = undefined;
  } else {
    next.conditionalOperator = field.conditionalOperator || 'equals';
    next.conditionalValue = field.conditionalValue?.trim();
  }
  if (!field.hideAndPrepopulate) {
    next.hideAndPrepopulate = undefined;
    next.prepopulatedValue = undefined;
  } else {
    next.prepopulatedValue = field.prepopulatedValue?.trim();
  }
  next.automationTriggers = TYPES_WITH_TRIGGERS.includes(type)
    ? (field.automationTriggers || []).filter((t) => t.triggerValue.trim()).map((t) => ({ ...t, triggerValue: t.triggerValue.trim(), automationDescription: t.automationDescription?.trim() || undefined }))
    : undefined;
  if (next.automationTriggers && next.automationTriggers.length === 0) next.automationTriggers = undefined;
  next.usedForReporting = field.usedForReporting || undefined;
  next.usedForAutomation = field.usedForAutomation || undefined;
  next.automationUseCase = field.usedForAutomation ? field.automationUseCase?.trim() || undefined : undefined;
  if (type === 'asset') {
    next.flaggedForConsultant = true;
    next.consultantReviewReason = 'asset_field_setup';
  } else if (field.consultantReviewReason === 'asset_field_setup') {
    next.flaggedForConsultant = undefined;
    next.consultantReviewReason = undefined;
  }
  return next;
}

/** The parts of a field the editor can change, in a stable shape, for "Save only when something changed". */
export function fieldSignature(field: FormField): string {
  const f = finalizeField(field);
  return JSON.stringify({
    name: f.name,
    portalLabel: f.portalLabel || '',
    type: f.type,
    required: !!f.required,
    helpText: f.helpText || '',
    options: f.options || [],
    cascadingOptions: f.cascadingOptions || [],
    defaultValue: f.defaultValue || '',
    conditionalParent: f.conditionalParent || '',
    conditionalOperator: f.conditionalOperator || '',
    conditionalValue: f.conditionalValue || '',
    hideAndPrepopulate: !!f.hideAndPrepopulate,
    prepopulatedValue: f.prepopulatedValue || '',
    automationTriggers: (f.automationTriggers || []).map((t) => [t.triggerValue, t.automationDescription || '']),
    usedForReporting: !!f.usedForReporting,
    usedForAutomation: !!f.usedForAutomation,
    automationUseCase: f.automationUseCase || '',
  });
}

export function hasFieldChanged(original: FormField, current: FormField): boolean {
  return fieldSignature(original) !== fieldSignature(current);
}

/** A sentence for the card and the preview: Only shown when "Department" equals "IT". */
export function describeCondition(field: FormField, allFields: Pick<FormField, 'id' | 'name'>[]): string | null {
  if (!field.conditionalParent) return null;
  const parent = allFields.find((f) => f.id === field.conditionalParent);
  return `Only shown when "${parent?.name || 'a removed field'}" ${operatorLabel(field.conditionalOperator)} "${field.conditionalValue || ''}"`;
}

// ---------------------------------------------------------------------------
// The saved record (`fields[]` on a request type, kept by serviceProjectRoutes)
// ---------------------------------------------------------------------------

export interface SavedRequestTypeField {
  id: string;
  fieldName: string;
  portalDisplayName?: string;
  fieldType: string;
  required: boolean;
  helpText?: string;
  options?: string[];
  optionsRawInput?: string;
  cascadingOptions?: CascadingOption[];
  defaultValue?: string;
  conditionalShowHide?: { parentFieldId: string; operator?: ConditionOperator; parentFieldValue: string };
  hideAndPrepopulate?: { hidden: boolean; prepopulatedValue: string };
  automationTriggers?: AutomationTrigger[];
  usedForReporting?: boolean;
  usedForAutomation?: boolean;
  automationUseCase?: string;
  requiresCustomField?: boolean;
  flaggedForConsultant?: boolean;
  consultantReviewReason?: string;
  order: number;
  isSystem?: boolean;
  aiGenerated?: boolean;
}

export function toSavedField(f: FormField, order: number): SavedRequestTypeField {
  return {
    id: f.id,
    fieldName: f.name,
    portalDisplayName: f.portalLabel,
    fieldType: f.type,
    required: !!f.required,
    helpText: f.helpText,
    options: f.options,
    optionsRawInput: f.optionsRawInput,
    cascadingOptions: f.cascadingOptions,
    defaultValue: f.defaultValue,
    conditionalShowHide: f.conditionalParent
      ? { parentFieldId: f.conditionalParent, operator: f.conditionalOperator || 'equals', parentFieldValue: f.conditionalValue || '' }
      : undefined,
    hideAndPrepopulate: f.hideAndPrepopulate ? { hidden: true, prepopulatedValue: f.prepopulatedValue || '' } : undefined,
    automationTriggers: f.automationTriggers,
    usedForReporting: f.usedForReporting || undefined,
    usedForAutomation: f.usedForAutomation || undefined,
    automationUseCase: f.automationUseCase,
    requiresCustomField: needsCustomField(f) || undefined,
    flaggedForConsultant: f.flaggedForConsultant || undefined,
    consultantReviewReason: f.consultantReviewReason,
    order,
    isSystem: f.isSystem,
    aiGenerated: f.aiGenerated,
  };
}

/** Read a saved field (today's shape, or an older record that stored `name`/`type` or the pre-November names). */
export function fromSavedField(raw: any, index: number): FormField {
  const type = normalizeFieldType(raw.fieldType || raw.type);
  const cascading: CascadingOption[] | undefined = Array.isArray(raw.cascadingOptions)
    ? raw.cascadingOptions.map((c: any) => ({
        parentValue: c.parentValue ?? c.parentOption ?? '',
        childValues: c.childValues ?? c.childOptions ?? [],
      }))
    : undefined;
  const condition = raw.conditionalShowHide || raw.conditionalDisplay || raw.conditionalVisibility;
  const hidden = raw.hideAndPrepopulate;
  return {
    id: raw.id || `field_${index}`,
    name: raw.fieldName ?? raw.name ?? '',
    portalLabel: raw.portalDisplayName ?? raw.portalLabel,
    type,
    required: !!raw.required,
    helpText: raw.helpText,
    options: raw.options ?? raw.selectOptions,
    optionsRawInput: raw.optionsRawInput,
    cascadingOptions: cascading,
    defaultValue: raw.defaultValue,
    conditionalParent: raw.conditionalParent ?? condition?.parentFieldId ?? condition?.fieldId,
    conditionalOperator: raw.conditionalOperator ?? condition?.operator ?? (condition ? 'equals' : undefined),
    conditionalValue: raw.conditionalValue ?? condition?.parentFieldValue ?? condition?.value ?? condition?.fieldValue,
    hideAndPrepopulate: typeof hidden === 'object' && hidden ? !!hidden.hidden : !!hidden || !!raw.hiddenFromPortal,
    prepopulatedValue: typeof hidden === 'object' && hidden ? hidden.prepopulatedValue : raw.prepopulatedValue ?? raw.hiddenStaticValue,
    automationTriggers: raw.automationTriggers,
    usedForReporting: raw.usedForReporting,
    usedForAutomation: raw.usedForAutomation,
    automationUseCase: raw.automationUseCase,
    flaggedForConsultant: raw.flaggedForConsultant,
    consultantReviewReason: raw.consultantReviewReason,
    order: typeof raw.order === 'number' ? raw.order : index,
    isSystem: raw.isSystem,
    aiGenerated: raw.aiGenerated,
  };
}
